import { round } from 'lodash';

import { SalesType } from '@client/new/common/types';
import { ArrayItem, SkipNull } from '@client/new/helpers/types';
import {
  AccessType,
  BundleItemType,
  BundlePricing,
  BundleStatus,
  FeatureCode,
  ProgramBusinessModel,
  ProgramsGetListQuery,
} from 'generated/graphql';

import {
  CreatorMode,
  ProgramBundle,
  ProgramBundleStatus,
  ProgramBusinessModelKind,
  ProgramItem,
  ProgramMediaCover,
  ProgramMetrics,
  ProgramOffer,
  ProgramOfferState,
  ProgramReadiness,
  ProgramsCreator,
  ProgramsDomain,
  ProgramStateKind,
  ProgramStatus,
  SubscriptionSublabel,
} from './programs.types';

// ---------------------------------------------------------------------------
// Raw server aliases
// ---------------------------------------------------------------------------

export type RawProgram = SkipNull<
  ArrayItem<ProgramsGetListQuery['collections']>
>;

/**
 * Local extension of the generated `Collection` shape to include
 * `readiness`. The field is wired into `programs.graphql`'s
 * `programsListItem` fragment, but the generated type does not yet reflect
 * it until the backend deploys `Program.readiness` and `yarn generate:graphql`
 * picks up the schema change. Until then, this narrow extension keeps a
 * single cast point at the parse boundary instead of leaking `any` into the
 * mapper internals.
 *
 * TODO(sc-111860): remove this and `WithReadiness` once the generated
 * `Collection` type exposes `readiness` directly (post backend deploy +
 * regenerated schema.graphql + `yarn generate:graphql`).
 */
type RawProgramReadiness = {
  title: boolean;
  cover: boolean;
  workouts: boolean;
  goal: boolean;
};

type WithReadiness<T> = T & { readiness?: RawProgramReadiness | null };

export type RawWorkout = SkipNull<ArrayItem<SkipNull<RawProgram['workouts']>>>;

export type RawBundle = SkipNull<ArrayItem<ProgramsGetListQuery['bundle']>>;

export type RawCreator = SkipNull<ProgramsGetListQuery['creator']>;

// ---------------------------------------------------------------------------
// Individual pure helpers
// ---------------------------------------------------------------------------

/**
 * Translates the generated `ProgramBusinessModel` enum into the
 * domain-side string union so downstream layers never import the
 * generated GraphQL types.
 */
export const toProgramBusinessModelKind = (
  raw: ProgramBusinessModel | null | undefined,
): ProgramBusinessModelKind | null => {
  if (raw === ProgramBusinessModel.Subscription) return 'subscription';
  if (raw === ProgramBusinessModel.Purchase) return 'purchase';
  if (raw === ProgramBusinessModel.Unassigned) return 'unassigned';
  return null;
};

/**
 * Translates the generated `BundleStatus` enum to the domain string union.
 */
export const toProgramBundleStatus = (
  raw: BundleStatus | null | undefined,
): ProgramBundleStatus => {
  if (raw === BundleStatus.Published) return 'PUBLISHED';
  if (raw === BundleStatus.Archived) return 'ARCHIVED';
  return 'DRAFT';
};

/**
 * Derives the wire-value program status from server-side publish/archive
 * timestamps. This is the raw projection of backend state — not the
 * creator-facing lifecycle (see `deriveProgramState`).
 *
 *  - `archivedAt` set AFTER `publishedAt` means the program was published
 *    once, then pulled back → `archived`.
 *  - `publishedAt` set and no archive → `published`.
 *  - Neither timestamp → `draft`.
 *  - `archivedAt` set without `publishedAt` is treated as `draft` (the
 *    program never went live, so there is nothing to archive).
 */
export const toProgramStatus = (p: {
  publishedAt?: string | null;
  archivedAt?: string | null;
}): ProgramStatus => {
  if (p.archivedAt) return 'archived';
  if (p.publishedAt) return 'published';
  return 'draft';
};

/**
 * Derives the creator-facing lifecycle from the mapped `ProgramItem`-shaped
 * inputs: wire `status` + business model + bundle membership + (legacy only)
 * `isVisible`.
 *
 * Rules:
 *  - `status === 'archived'` → `'archived'` (terminal).
 *  - `status === 'draft'`    → `'draft'`.
 *  - Legacy creators (no bundle/offer abstraction): `published-wire +
 *    isVisible` → `'published'`; otherwise `'draft'`. `isVisible` is
 *    backend-controlled (no UI toggle).
 *  - New creators: `status === 'published'` AND at least one PUBLISHED offer
 *    contains the program → `'published'`. Subscription = live subscription
 *    bundle membership; OTP = `isActive: true` offer in `offerState`, OR a
 *    standalone exclusive checkout (legacy OTP pattern: program sold via its
 *    own `exclusiveProgramMetadata.checkoutPageUrl`, no bundle wrapper).
 *    Published-wire but no live offer → `'draft'` (the program is publishable
 *    but not yet sold, treated as draft from a sales POV).
 */
export const deriveProgramState = (params: {
  status: ProgramStatus;
  programBusinessModel: ProgramBusinessModel | null;
  offerState: ProgramOfferState;
  programId: number;
  bundles: ReadonlyArray<RawBundle>;
  creatorMode: CreatorMode;
  isVisible: boolean;
  hasStandaloneCheckout: boolean;
}): ProgramStateKind => {
  if (params.status === 'archived') return 'archived';
  if (params.status === 'draft') return 'draft';
  if (params.creatorMode === 'legacy') {
    return params.isVisible ? 'published' : 'draft';
  }
  if (params.programBusinessModel === ProgramBusinessModel.Subscription) {
    return isLiveSubscriptionBundleWithProgram(params.programId, params.bundles)
      ? 'published'
      : 'draft';
  }
  // OTP programs may sell via a bundle offer, a standalone exclusive checkout,
  // or both simultaneously during the legacy → bundle migration. Either signal
  // is enough to count the program as live.
  const hasLiveOtpSale =
    hasLiveOtpOffer(params.offerState) || params.hasStandaloneCheckout;
  return hasLiveOtpSale ? 'published' : 'draft';
};

/**
 * Derives the Offers-column shape for a specific program by combining its
 * business model with the subset of creator bundles that actually include
 * the program as an item.
 *
 * Invariants:
 *  - `Subscription` short-circuits to `{type: 'subscription'}` regardless of
 *    bundles (subscription programs are exclusive by backend contract).
 *  - For everything else, only OTP bundles whose `items` contain
 *    `{itemType: PROGRAM, itemId: programId}` count as membership. Bundles
 *    without `items` (legacy payloads) contribute nothing.
 *  - An empty result maps to `{type: 'none'}` so the UI shows the add CTA.
 */
const toSubscriptionSublabel = (
  programId: number,
  bundles: ReadonlyArray<RawBundle>,
): SubscriptionSublabel => {
  const matchingBundles = bundles.filter(
    (b) =>
      b.pricing === BundlePricing.Subscription &&
      bundleContainsProgram(b, programId),
  );

  const hasExclusive = matchingBundles.some((b) => !b.isPlatformBundle);
  const hasPlatform = matchingBundles.some((b) => b.isPlatformBundle);

  if (hasExclusive && hasPlatform) return 'exclusive-and-platform';
  if (hasPlatform) return 'platform';
  if (hasExclusive) return 'exclusive';
  return null;
};

export const toProgramOfferState = (
  programId: number,
  businessModel: ProgramBusinessModel | null | undefined,
  bundles: ReadonlyArray<RawBundle>,
  isProgramArchived = false,
  featureCodeById: FeatureCodeById = new Map(),
): ProgramOfferState => {
  if (businessModel === ProgramBusinessModel.Subscription) {
    return {
      type: 'subscription',
      sublabel: toSubscriptionSublabel(programId, bundles),
    };
  }

  const visibleOffers: Array<ProgramOffer> = bundles
    .filter(isOtpBundle)
    .filter((b) => isVisibleOtpBundle(b, isProgramArchived))
    .filter((b) => bundleContainsProgram(b, programId))
    .map((b) => toProgramOffer(b, featureCodeById))
    .sort((a, b) => a.id - b.id);

  if (visibleOffers.length === 0) return { type: 'none' };
  return { type: 'otp', offers: visibleOffers };
};

// OTP bundles only; subscription bundles are handled by the program's
// business model, not by the bundle items list.
const isOtpBundle = (b: RawBundle): boolean =>
  // `pricing` may be absent in legacy payloads — fall back to "looks OTP" so
  // older cached responses still render sensibly.
  b.pricing === undefined || b.pricing === null
    ? true
    : b.pricing === BundlePricing.Otp;

const isVisibleOtpBundle = (
  b: RawBundle,
  isProgramArchived: boolean,
): boolean => {
  if (b.status === BundleStatus.Published || b.status === BundleStatus.Draft)
    return true;
  return isProgramArchived && b.status === BundleStatus.Archived;
};

// A bundle counts as live only when it is Published.
const isLiveBundle = (b: RawBundle): boolean =>
  b.status === BundleStatus.Published;

const bundleContainsProgram = (b: RawBundle, programId: number): boolean => {
  const items = b.items ?? [];
  return items.some(
    (item) =>
      item &&
      item.itemType === BundleItemType.Program &&
      item.itemId === programId,
  );
};

// Display names for bundle feature items, mapped from their FeatureCode.
const FEATURE_NAMES: Record<FeatureCode, string> = {
  [FeatureCode.Community]: 'Community',
  [FeatureCode.Dm]: 'DM',
  [FeatureCode.Insight]: 'Insights',
  [FeatureCode.Livestream]: 'Livestream',
  [FeatureCode.Nutrition]: 'Nutrition',
  [FeatureCode.Story]: 'Stories',
};

// Maps a creator's feature id -> FeatureCode, used to resolve the FeatureCode
// of a bundle FEATURE item (which only carries the numeric `itemId`).
export type FeatureCodeById = ReadonlyMap<number, FeatureCode>;

export type BundleContentItemsSource = {
  items?: ReadonlyArray<{
    itemType: BundleItemType;
    itemId?: number | null;
  }> | null;
};

// Summarises a bundle's contents as e.g. "5 programs • 2 Extras • DM • Community".
// Programs and Extras (CONTENT) are counted; FEATURE items are listed by name.
export const toBundleContentDescription = (
  b: BundleContentItemsSource,
  featureCodeById: FeatureCodeById,
): string => {
  const items = b.items ?? [];

  const programCount = items.filter(
    (i) => i.itemType === BundleItemType.Program,
  ).length;
  const extrasCount = items.filter(
    (i) => i.itemType === BundleItemType.Content,
  ).length;
  const featureNames = items
    .filter((i) => i.itemType === BundleItemType.Feature)
    .map((i) => (i.itemId != null ? featureCodeById.get(i.itemId) : undefined))
    .filter((code): code is FeatureCode => Boolean(code))
    .map((code) => FEATURE_NAMES[code]);

  return [
    ...(programCount > 0
      ? [`${programCount} ${programCount === 1 ? 'Program' : 'Programs'}`]
      : []),
    ...(extrasCount > 0
      ? [`${extrasCount} ${extrasCount === 1 ? 'Extra' : 'Extras'}`]
      : []),
    ...featureNames,
  ].join(' • ');
};

const toProgramOffer = (
  b: RawBundle,
  featureCodeById: FeatureCodeById,
): ProgramOffer => ({
  id: b.id,
  name: b.title,
  isActive: isLiveBundle(b),
  status: toProgramBundleStatus(b.status),
  deeplinkUrl: b.deeplinkUrl ?? null,
  landingPageUrl: b.landingPageUrl ?? null,
  checkoutUrl: b.checkoutPage?.url ?? null,
  priceInCents: b.oneTimeProduct?.price ?? null,
  isFree: b.isFree,
  contentDescription: toBundleContentDescription(b, featureCodeById),
});

const hasLiveOtpOffer = (offerState: ProgramOfferState): boolean =>
  offerState.type === 'otp' &&
  offerState.offers.some((offer) => offer.isActive);

/**
 * Aggregates program-level metrics. `avgRating` comes straight from the
 * server-provided `programStats`. The other three are summed across
 * workouts (mirrors `toCollectionStats` in `collectionMappers.ts`).
 *
 * Nullability semantics:
 *  - Empty/missing `workouts` → `null` for begins/completions/views.
 *  - `rating` is the unweighted mean of each workout's `statsRatingAverage`,
 *    matching the legacy collections page; `null` when no workout has ratings.
 *  - Individual null workout metrics count as 0 in the sum, but the final
 *    result is `null` when ALL workouts are null for that metric.
 */
export const toProgramMetrics = (
  p: Pick<RawProgram, 'workouts' | 'programStats'>,
): ProgramMetrics => {
  const workouts = (p.workouts || []).filter((w): w is RawWorkout =>
    Boolean(w),
  );

  return {
    rating: averageWorkoutRatings(workouts),
    begins: sumNullable(workouts, (w) => w.statsBegins),
    completions: sumNullable(workouts, (w) => w.statsCompletions),
    views: p.programStats ? p.programStats.statsViews : null,
  };
};

const averageWorkoutRatings = (
  workouts: ReadonlyArray<RawWorkout>,
): number | null => {
  const ratings = workouts
    .map((w) => w.statsRatingAverage)
    .filter((r): r is number => typeof r === 'number' && r > 0);
  if (ratings.length === 0) return null;
  return ratings.reduce((acc, r) => acc + r, 0) / ratings.length;
};

const sumNullable = <T>(
  items: ReadonlyArray<T>,
  getValue: (item: T) => number | null | undefined,
): number | null => {
  if (items.length === 0) return null;
  const defined = items
    .map(getValue)
    .filter((v): v is number => v !== null && v !== undefined);
  if (defined.length === 0) return null;
  return defined.reduce((acc, v) => acc + v, 0);
};

export const toCommentsCount = (p: Pick<RawProgram, 'workouts'>): number => {
  const workouts = (p.workouts || []).filter((w): w is RawWorkout =>
    Boolean(w),
  );
  return workouts.reduce((acc, w) => acc + (w.statsComments || 0), 0);
};

/**
 * Human-readable program "subinfo" string: structure label + workout count.
 * Matches the extras list's secondary row (e.g. "Week/Day • 12 workouts").
 */
export const toSubInfo = (
  p: Pick<RawProgram, 'workouts' | 'contentStructure'>,
): string => {
  const count = (p.workouts || []).filter(Boolean).length;
  const structure = toStructureLabel(p.contentStructure);
  const countLabel = `${count} ${count === 1 ? 'workout' : 'workouts'}`;
  return structure ? `${structure} • ${countLabel}` : countLabel;
};

const toStructureLabel = (
  structure: string | null | undefined,
): string | null => {
  if (!structure) return null;
  switch (structure.toUpperCase()) {
    case 'WEEK_DAY':
    case 'DAYWEEK':
      return 'Week/Day';
    case 'SERIES':
    case 'SEQUENTIAL':
      return 'Series';
    case 'MODULE':
      return 'Module';
    default:
      // Fallback: prettify the enum-like value ("ON_DEMAND" → "On Demand").
      return structure
        .toLowerCase()
        .split(/[_\s]+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
  }
};

/**
 * Derives the Model's `salesType` from the program's business model and
 * backend accessType. Subscription programs are always `'subscription'`.
 * Everything else (Purchase / Unassigned) is `'otp'` — the legacy reorder
 * mutation sorts OTP and subscription programs into separate buckets, so
 * this is also the bucket the Model uses when applying an optimistic
 * reorder overlay.
 */
export const toSalesType = (
  businessModel: ProgramBusinessModel | null | undefined,
  accessType: string | null | undefined,
): SalesType => {
  if (businessModel === ProgramBusinessModel.Subscription)
    return 'subscription';
  if (accessType === AccessType.Premium) return 'subscription';
  return 'otp';
};

export const toMediaCover = (
  media: RawProgram['mediaCover'],
): ProgramMediaCover | null => {
  if (!media) return null;
  const cdn01 = media.cdn01 ?? '';
  const image = media.image ?? '';
  if (!cdn01 && !image) return null;
  return { cdn01, image };
};

type RawChallenges = RawProgram['challenges'];

export const toHasChallenges = (challenges: RawChallenges): boolean =>
  (challenges || []).filter(Boolean).length > 0;

export const toHasOngoingChallenges = (challenges: RawChallenges): boolean =>
  (challenges || []).some((ch) => {
    if (!ch) return false;
    const end = new Date(ch.completionEnd);
    return end.getTime() > Date.now();
  });

const isLiveSubscriptionBundleWithProgram = (
  programId: number,
  bundles: ReadonlyArray<RawBundle>,
): boolean =>
  bundles.some(
    (b) =>
      b.status === BundleStatus.Published &&
      b.pricing === BundlePricing.Subscription &&
      bundleContainsProgram(b, programId),
  );

/**
 * True when the program cannot be reached by consumers in the app. See
 * `ProgramItem.notVisibleToConsumers` for the full semantics.
 *
 * Subscription programs always map to `offerState: { type: 'subscription' }`
 * regardless of bundle membership (invariant of `toProgramOfferState`), so
 * `offerState` cannot answer the visibility question for them -- bundle
 * membership is checked explicitly. For every other business model
 * (`OTP`, `Exclusive`, `Unassigned`), visibility depends on whether the row
 * has at least one live OTP offer; draft-only offer membership is still not
 * consumer-visible.
 */
export const toNotVisibleToConsumers = (params: {
  status: ProgramStatus;
  programBusinessModel: ProgramBusinessModel | null;
  offerState: ProgramOfferState;
  programId: number;
  bundles: ReadonlyArray<RawBundle>;
  creatorMode: CreatorMode;
  isVisible: boolean;
  hasStandaloneCheckout: boolean;
}): boolean => {
  if (params.status !== 'published') return true;
  // Legacy creators publish straight to the consumer — `isVisible` alone
  // decides whether the program is reachable, the bundle/offer abstraction
  // does not apply.
  if (params.creatorMode === 'legacy') {
    return !params.isVisible;
  }
  if (params.programBusinessModel === ProgramBusinessModel.Subscription) {
    return !isLiveSubscriptionBundleWithProgram(
      params.programId,
      params.bundles,
    );
  }
  return !(hasLiveOtpOffer(params.offerState) || params.hasStandaloneCheckout);
};

/**
 * Computes the rounded average rating the UI should display, matching the
 * one-decimal format used by the collections list.
 */
export const toRoundedRating = (avg: number | null): number | null =>
  avg === null ? null : round(avg, 1);

// ---------------------------------------------------------------------------
// Readiness
// ---------------------------------------------------------------------------

/**
 * Pure passthrough — the GraphQL shape is already `Record<field, boolean>`
 * by construction (one non-null Boolean field per readiness criterion). We
 * still reconstruct the object explicitly so that the domain side cannot
 * drift if the backend ever returns extra unrelated fields.
 */
export const toProgramReadiness = (
  raw: RawProgramReadiness | null | undefined,
): ProgramReadiness | null => {
  if (!raw) return null;
  return {
    title: raw.title,
    cover: raw.cover,
    workouts: raw.workouts,
    goal: raw.goal,
  };
};

// ---------------------------------------------------------------------------
// Composite glue
// ---------------------------------------------------------------------------

export const toProgramItem = (
  rawInput: RawProgram,
  bundles: ReadonlyArray<RawBundle>,
  creatorMode: CreatorMode,
  featureCodeById: FeatureCodeById = new Map(),
): ProgramItem => {
  // See `WithReadiness` comment above for why this widening lives here.
  const raw: WithReadiness<RawProgram> = rawInput;
  const status = toProgramStatus({
    publishedAt: raw.publishedAt ?? null,
    archivedAt: raw.archivedAt ?? null,
  });
  const offerState = toProgramOfferState(
    raw.id,
    raw.programBusinessModel,
    bundles,
    status === 'archived',
    featureCodeById,
  );
  const metrics = toProgramMetrics(raw);
  const programBusinessModel = toProgramBusinessModelKind(
    raw.programBusinessModel,
  );
  const hasStandaloneCheckout = Boolean(
    raw.exclusiveProgramMetadata?.checkoutPageUrl,
  );
  const state = deriveProgramState({
    status,
    programBusinessModel: raw.programBusinessModel ?? null,
    offerState,
    programId: raw.id,
    bundles,
    creatorMode,
    isVisible: raw.isVisible ?? false,
    hasStandaloneCheckout,
  });
  return {
    id: raw.id,
    name: raw.name || '',
    status,
    state,
    mediaCover: toMediaCover(raw.mediaCover ?? null),
    subInfo: toSubInfo(raw),
    offerState,
    metrics: { ...metrics, rating: toRoundedRating(metrics.rating) },
    commentsCount: toCommentsCount(raw),
    salesType: toSalesType(raw.programBusinessModel, raw.accessType),
    index: raw.index ?? null,
    deeplinkUrl:
      raw.exclusiveProgramMetadata?.deeplinkUrl ?? raw.deeplinkUrl ?? null,
    programBusinessModel,
    notVisibleToConsumers: toNotVisibleToConsumers({
      status,
      programBusinessModel: raw.programBusinessModel ?? null,
      offerState,
      programId: raw.id,
      bundles,
      creatorMode,
      isVisible: raw.isVisible ?? false,
      hasStandaloneCheckout,
    }),
    hasStandaloneCheckout,
    hasChallenges: toHasChallenges(raw.challenges),
    hasOngoingChallenges: toHasOngoingChallenges(raw.challenges),
    readiness: toProgramReadiness(raw.readiness),
  };
};

const toProgramBundle = (b: RawBundle): ProgramBundle => ({
  id: b.id,
  title: b.title,
  isPlatformBundle: b.isPlatformBundle,
  status: toProgramBundleStatus(b.status),
});

/**
 * The creator GraphQL field is a plain string ('legacy' | 'bundle'); any
 * value other than the explicit `legacy` marker falls through to `new`.
 */
export const toCreatorMode = (
  businessModel: string | null | undefined,
): CreatorMode => (businessModel === 'legacy' ? 'legacy' : 'new');

const toCreator = (
  creator: RawCreator | null | undefined,
  fallbackId: number,
): ProgramsCreator => ({
  id: creator?.id ?? fallbackId,
  customURL: creator?.customURL ?? '',
  profileDeepLink: creator?.profileDeepLink ?? '',
  mode: toCreatorMode(creator?.businessModel),
});

/**
 * Top-level mapper: ProgramsGetListQuery → ProgramsDomain.
 *
 * Curried because the creator id is a caller-side input (comes from the
 * hook's `influencerId` param) rather than from the query itself.
 */
export const toProgramsDomain =
  ({ creatorId }: { creatorId: number }) =>
  (raw: ProgramsGetListQuery): ProgramsDomain => {
    const bundles = (raw.bundle || []).filter((b): b is RawBundle =>
      Boolean(b),
    );
    const collections = (raw.collections || []).filter((c): c is RawProgram =>
      Boolean(c),
    );
    const creator = toCreator(raw.creator, creatorId);
    const featureCodeById: FeatureCodeById = new Map(
      (raw.creator?.features ?? [])
        .filter((f): f is NonNullable<typeof f> => Boolean(f))
        .map((f) => [f.id, f.code] as const),
    );
    return {
      creator,
      bundles: bundles.map(toProgramBundle),
      programs: collections.map((c) =>
        toProgramItem(c, bundles, creator.mode, featureCodeById),
      ),
    };
  };
